import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { hasCoreManagementAccess } from '../config/accessControl';
import { API_BASE_URL, SERVER_BASE_URL } from '../config/api';

// ── Axios setup ──────────────────────────────────────────────
axios.defaults.baseURL = API_BASE_URL;

// Add response interceptor to handle errors globally
axios.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('dp_token');
      // Eğer zaten login sayfasında değilsek login'e yönlendir
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

axios.interceptors.request.use(cfg => {
  const token = localStorage.getItem('dp_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnread] = useState(0);

  // AuthContext.js içindeki fetchMe fonksiyonunu güncelleyin:

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await axios.get('/auth/me');
      if (data.success) {
        setUser(data.data);
        setUnread(data.data.okunmamis_bildirim || 0);
      } else {
        throw new Error("Yetkisiz erişim");
      }
    } catch (error) {
      localStorage.removeItem('dp_token');
      setUser(null);
      // Token varsa ama hata aldıysak (expire olmuşsa) yönlendir
      if (localStorage.getItem('dp_token')) {
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('dp_token');
    if (token) fetchMe();
    else setLoading(false);
  }, [fetchMe]);

  // Returns { success, user } or { success: false, message }
  const login = async (email, sifre) => {
    try {
      const { data } = await axios.post('/auth/login', { email, sifre });
      if (data.success) {
        localStorage.setItem('dp_token', data.data.token);
        setUser(data.data.user);
        setUnread(data.data.user?.okunmamis_bildirim || 0);
        return { success: true, user: data.data.user };
      }
      return { success: false, message: data.message || 'Giriş başarısız' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Sunucuya bağlanılamadı';
      return { success: false, message: msg };
    }
  };

  const logout = () => {
    localStorage.removeItem('dp_token');
    setUser(null);
    setUnread(0);
  };

  // Role helpers
  const isKurucu = user?.rol === 'kurucu';
  const isOgretmen = user?.rol === 'ogretmen';
  const isRehber = user?.rol === 'rehber_ogretmen';
  const isMuhasebe = user?.rol === 'muhasebe';
  const isIsleri = user?.rol === 'ogrenci_isleri';
  const isVeli = user?.rol === 'veli';
  const isOgrenci = user?.rol === 'ogrenci';
  const isAdmin = isKurucu || isIsleri;
  const canManageCoreModules = hasCoreManagementAccess(user?.rol);

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, fetchMe,
      unreadCount, setUnread,
      isKurucu, isOgretmen, isRehber, isMuhasebe, isIsleri, isVeli, isOgrenci,
      isAdmin, canManageCoreModules,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

// Named export so pages can do: import { axios, API_BASE_URL } from '...'
export { axios, API_BASE_URL, SERVER_BASE_URL };
